import { Injectable, OnDestroy } from '@angular/core';
import { VERSION } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { LoggingService, LogLevel } from './logging.service';
import { SocketService } from './socket.service';
import * as storageHelper from '../modules/core/helpers/storage-helper';
import { AppDetails, AppAnalyticsState, BrowserDetails, PerformanceMetrics, PushNotificationState, SocketConnectionState, AnalyticsEvent } from '@app/modules/shared/interfaces/app-analytics-state.type';
import { environment } from '../../environments/environment';
import { SwUpdate } from '@angular/service-worker';

@Injectable({
    providedIn: 'root'
})
export class AppAnalyticsService implements OnDestroy {
      static readonly COMPONENT_NAME = 'AppAnalyticsService';

    // Configuration constants
    private readonly CONFIG = {
        AUTO_LOG_INTERVAL: 60000,      // 60 seconds
        MONITORING_INTERVAL: 5000,     // 5 seconds
        BATCH_DELAY: 2000,             // 2 seconds
        STORAGE_EXPIRY: 7 * 24 * 60 * 60, // 7 days in seconds
        MEMORY_PRESSURE_THRESHOLD: 85, // 85%
        PRODUCTION_MODE: environment.production || environment.stage
    } as const;

    // Core state
    private appAnalyticsState: AppAnalyticsState;
    private analyticsEventsSubject = new Subject<AnalyticsEvent>();

    // Operational state
    private currentUserId: number | null = null;
    private timers = new Map<string, any>();
    private batchTimeout: any = null;
    private isDestroyed = false;
    private swUpdateSubscription?: Subscription;

    constructor(private loggingService: LoggingService, private socketService: SocketService, private swUpdate: SwUpdate) {
        this.appAnalyticsState = this.createInitialState();
        this.initializeService();
        this.initializeSwUpdateSubscription();
    }

    // Create initial state with safe browser API access
    private createInitialState(): AppAnalyticsState {
        const now = Date.now();

        return {
            socketConnectionState: {
                readyState: undefined,
                connectRetries: 0,
                lastConnectionAt: null,
                lastDisconnectionAt: null,
                firstConnectedAt: null,
                sessionStartTime: now,
                heartbeatStatus: { lastPong: 0, missedCount: 0 },
                totalConnectionAttempts: 0,
                totalReconnects: 0,
                totalDisconnects: 0,
                totalMessagesSent: 0,
                totalMessagesReceived: 0
            },
            performanceMetrics: {
                memoryPressure: false,
                channelsCount: 0,
                queueMemoryEstimate: 0,
                lastHealthCheck: 0
            },
            pushNotificationState: {
                subscribed: false,
                currentStatus: 'default',
                lastUpdated: now
            },
            browserDetails: this.getBrowserDetails(),
            appDetails: {
                version: environment.appVersion,
                currentUrl: this.getCurrentUrl(),
                appStartTime: now,
                environment: environment.production ? 'production' : environment.stage ? 'stage' : 'development',
                buildTimestamp: this.getBuildTimestamp(),
                angularVersion: VERSION.full,
                serviceWorkerEnabled: environment.production || environment.stage,
                serviceWorkerState: this.getServiceWorkerState(),
                ngswConfig: this.getNgswConfig()
            }
        };
    }

    // Get appropriate log level based on environment
    private getLogLevel(): LogLevel {
        return this.CONFIG.PRODUCTION_MODE ? LogLevel.WARN : LogLevel.DEBUG;
    }

    // Safe browser details extraction
    private getBrowserDetails(): BrowserDetails {
        const details: BrowserDetails = {
            platform: 'unknown',
            userAgent: 'unknown',
            language: 'en',
            timezone: 'unknown',
            screenResolution: 'unknown',
            cookieEnabled: false
        };

        if (typeof navigator !== 'undefined') {
            details.platform = navigator.platform || 'unknown';
            details.userAgent = navigator.userAgent || 'unknown';
            details.language = navigator.language || 'en';
            details.cookieEnabled = navigator.cookieEnabled || false;

            try {
                details.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
            } catch {
                // Keep default
            }
        }

        if (typeof screen !== 'undefined') {
            details.screenResolution = `${screen.width || 0}x${screen.height || 0}`;
        }

        return details;
    }

    // Safe current URL extraction
    private getCurrentUrl(): string {
        return (typeof window !== 'undefined' && window.location)
            ? window.location.href
            : 'unknown';
    }

    // Get build timestamp from ngsw-config - returns undefined initially, will be populated by SW updates
    private getBuildTimestamp(): string | undefined {
        // Return undefined initially - will be populated when SW update events occur
        return undefined;
    }

    // Get service worker state
    private getServiceWorkerState(): string {
        if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
            return navigator.serviceWorker.controller ? 'active' : 'registered';
        }
        return 'not-supported';
    }

    // Get ngsw config data - returns current version data if available, undefined otherwise
    private getNgswConfig(): AppDetails['ngswConfig'] {
        // Return current ngsw config if it has been captured, otherwise undefined
        return this.appAnalyticsState?.appDetails?.ngswConfig || undefined;
    }

    // Try to fetch ngsw.json to get current version info
    private tryFetchNgswConfig(): void {
        try {
            // Try to fetch the ngsw.json file which contains the actual appData
            fetch('/ngsw.json', { method: 'GET' })
                .then(response => {
                    if (response.ok) {
                        return response.json();
                    }
                    throw new Error('Failed to fetch ngsw.json');
                })
                .then(config => {
                    if (config && config.appData) {
                        const appData = config.appData;
                        this.updateAppDetails({
                            buildTimestamp: appData.buildDate || new Date().toISOString(),
                            ngswConfig: {
                                buildVersion: appData.buildVersion || 'unknown',
                                buildDate: appData.buildDate || new Date().toISOString(),
                                forceReload: appData.forceReload || false,
                                updateMessage: appData.updateMessage || 'Current version from ngsw.json'
                            }
                        });
                        this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                            'Captured version from ngsw.json:', appData);
                    }
                })
                .catch(error => {
                    this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                        'Could not fetch ngsw.json:', error);
                });
        } catch (error) {
            this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                'Error in tryFetchNgswConfig:', error);
        }
    }

    // Try to fetch ngsw/state to get detailed service worker status
    private tryFetchNgswState(): void {
        try {
            // Try to fetch the ngsw/state endpoint for detailed SW debug info
            fetch('/ngsw/state', { method: 'GET' })
                .then(response => {
                    if (response.ok) {
                        return response.text(); // ngsw/state returns plain text
                    }
                    throw new Error('Failed to fetch ngsw/state');
                })
                .then(stateText => {
                    if (stateText) {
                        // Parse the state text to extract key information
                        const stateInfo = this.parseNgswStateText(stateText);
                        this.updateAppDetails({
                            serviceWorkerState: stateInfo.driverState || this.getServiceWorkerState(),
                            ngswState: stateInfo
                        });
                        this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                            'Captured SW state from ngsw/state:', stateInfo);
                    }
                })
                .catch(error => {
                    this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                        'Could not fetch ngsw/state:', error);
                });
        } catch (error) {
            this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                'Error in tryFetchNgswState:', error);
        }
    }

    // Parse the ngsw/state text response to extract structured data
    private parseNgswStateText(stateText: string): any {
        const lines = stateText.split('\n');
        const stateInfo: any = {
            driverVersion: null,
            driverState: null,
            latestManifestHash: null,
            lastUpdateCheck: null,
            versions: []
        };

        let currentVersion: { hash?: string; clients: string[] } | null = null;
        let parsingVersions = false;

        for (const line of lines) {
            const trimmed = line.trim();

            // Extract driver info
            if (trimmed.startsWith('Driver version:')) {
                stateInfo.driverVersion = trimmed.replace('Driver version:', '').trim();
            } else if (trimmed.startsWith('Driver state:')) {
                stateInfo.driverState = trimmed.replace('Driver state:', '').trim();
            } else if (trimmed.startsWith('Latest manifest hash:')) {
                stateInfo.latestManifestHash = trimmed.replace('Latest manifest hash:', '').trim();
            } else if (trimmed.startsWith('Last update check:')) {
                stateInfo.lastUpdateCheck = trimmed.replace('Last update check:', '').trim();
            }

            // Extract version info
            else if (trimmed.startsWith('=== Version')) {
                currentVersion = {
                    hash: trimmed.match(/=== Version ([a-f0-9]+) ===/)?.[1],
                    clients: []
                };
                stateInfo.versions.push(currentVersion);
                parsingVersions = true;
            } else if (parsingVersions && trimmed.startsWith('Clients:')) {
                const clientIds = trimmed.replace('Clients:', '').trim().split(', ');
                if (currentVersion) {
                    currentVersion.clients = clientIds.filter(id => id.length > 0);
                }
            }
        }

        return stateInfo;
    }

    // Initialize service with error handling
    private initializeService(): void {
        if (this.isDestroyed) return;

        try {
            this.emitAnalyticsEvent('session_start', { timestamp: Date.now() });
            this.startTimer('autoLog', this.CONFIG.AUTO_LOG_INTERVAL, () => this.performAutoLog());
            this.startTimer('monitoring', this.CONFIG.MONITORING_INTERVAL, () => this.performSelfMonitoring());
            this.startTimer('serverSync', this.CONFIG.AUTO_LOG_INTERVAL, () => {
                this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                    'serverSync timer triggered, calling sendAnalyticsToServer');
                this.sendAnalyticsToServer();
            });
            this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME, 'Service initialized successfully');
        } catch (error) {
            this.loggingService.log(LogLevel.ERROR, AppAnalyticsService.COMPONENT_NAME, 'Failed to initialize service:', error);
        }
    }

    // Initialize service worker update subscription to capture real appData
    private initializeSwUpdateSubscription(): void {
        if (this.swUpdate && this.swUpdate.isEnabled) {
            // First, try to get the current activated version's appData immediately
            this.captureCurrentVersionAppData();

            // Then subscribe to version updates for future changes
            this.swUpdateSubscription = this.swUpdate.versionUpdates.subscribe((event: any) => {
                this.loggingService.log(this.getLogLevel(), AppAnalyticsService.COMPONENT_NAME,
                    '[SW Update Event]:', event.type, event);

                if (event.version && event.version.appData) {
                    const appData = event.version.appData;
                    this.updateAppDetails({
                        buildTimestamp: appData.buildDate,
                        ngswConfig: {
                            buildVersion: appData.buildVersion,
                            buildDate: appData.buildDate,
                            forceReload: appData.forceReload,
                            updateMessage: appData.updateMessage
                        }
                    });
                    this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                        'Updated app details with real ngsw appData:', appData);
                }
            });
        } else {
            this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                'Service Worker updates not enabled, using default ngsw config');
        }
    }

    // Capture current activated version's appData immediately
    private captureCurrentVersionAppData(): void {
        try {
            this.tryFetchNgswConfig();
            this.tryFetchNgswState();
            this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                'Attempted to capture current version appData from ngsw.json and ngsw/state');
        } catch (error) {
            this.loggingService.log(LogLevel.WARN, AppAnalyticsService.COMPONENT_NAME,
                'Error capturing current version appData:', error);
        }
    }

    // Generic update method with validation
    private updateState<T extends keyof AppAnalyticsState>(
        key: T,
        updates: Partial<AppAnalyticsState[T]>,
        validator?: (updates: any) => boolean
    ): void {
        if (this.isDestroyed || !updates || typeof updates !== 'object') {
            return;
        }

        if (validator && !validator(updates)) {
            this.loggingService.log(LogLevel.WARN, AppAnalyticsService.COMPONENT_NAME,
                `Invalid updates provided to update${key}`);
            return;
        }

        try {
            (this.appAnalyticsState as any)[key] = {
                ...(this.appAnalyticsState as any)[key],
                ...updates
            };
        } catch (error) {
            this.loggingService.log(LogLevel.ERROR, AppAnalyticsService.COMPONENT_NAME,
                `Error updating ${key}:`, error);
        }
    }

    // Public update methods using generic updater
    updateSocketConnectionState(updates: Partial<SocketConnectionState>): void {
        this.updateState('socketConnectionState', updates);
    }

    updatePerformanceMetrics(updates: Partial<PerformanceMetrics>): void {
        // Add memory calculation if not provided
        if (!updates.memoryUsage && typeof performance !== 'undefined' && 'memory' in performance) {
            try {
                const memInfo = (performance as any).memory;
                if (memInfo && typeof memInfo.usedJSHeapSize === 'number' &&
                    typeof memInfo.totalJSHeapSize === 'number' && memInfo.totalJSHeapSize > 0) {
                    const usagePercent = (memInfo.usedJSHeapSize / memInfo.totalJSHeapSize) * 100;
                    updates.memoryUsage = {
                        usedJSHeapSize: memInfo.usedJSHeapSize,
                        totalJSHeapSize: memInfo.totalJSHeapSize,
                        usagePercent
                    };
                    updates.memoryPressure = usagePercent > this.CONFIG.MEMORY_PRESSURE_THRESHOLD;
                    updates.lastHealthCheck = Date.now();
                }
            } catch {
                // Memory API not available
            }
        }
        this.updateState('performanceMetrics', updates);
    }

    updatePushNotificationState(updates: Partial<PushNotificationState>): void {
        this.updateState('pushNotificationState', { ...updates, lastUpdated: Date.now() });
    }

    updateBrowserDetails(updates: Partial<BrowserDetails>): void {
        this.updateState('browserDetails', updates);
    }

    updateAppDetails(updates: Partial<AppDetails>): void {
        this.updateState('appDetails', updates);
    }

    // Clear all timers safely - optimized
    private clearAllTimers(): void {
        this.timers.forEach((timer, name) => {
            clearInterval(timer);
        });
        this.timers.clear();

        if (this.batchTimeout) {
            clearTimeout(this.batchTimeout);
            this.batchTimeout = null;
        }
    }

    // Start a named timer with interval
    private startTimer(name: string, interval: number, callback: () => void): void {
        if (this.isDestroyed || !name || typeof interval !== 'number' || interval <= 0) {
            this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                `startTimer: Skipping timer '${name}' - destroyed: ${this.isDestroyed}, name: ${name}, interval: ${interval}`);
            return;
        }

        // Clear existing timer if it exists
        this.stopTimer(name);

        try {
            const timer = setInterval(() => {
                if (!this.isDestroyed) {
                    callback();
                }
            }, interval);

            this.timers.set(name, timer);
            this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                `startTimer: Timer '${name}' started with interval ${interval}ms`);
        } catch (error) {
            this.loggingService.log(LogLevel.ERROR, AppAnalyticsService.COMPONENT_NAME,
                `Failed to start timer '${name}':`, error);
        }
    }

    // Stop a specific timer
    private stopTimer(name: string): void {
        const timer = this.timers.get(name);
        if (timer) {
            clearInterval(timer);
            this.timers.delete(name);
        }
    }

    // Self-monitoring to gather data independently
    private performSelfMonitoring(): void {
        try {
            // Update current URL safely
            if (typeof window !== 'undefined' && window.location) {
                this.updateAppDetails({ currentUrl: window.location.href });
            }

            // Auto-calculate memory usage if available
            if (typeof performance !== 'undefined' && 'memory' in performance) {
                try {
                    const memInfo = (performance as any).memory;
                    if (memInfo && typeof memInfo.usedJSHeapSize === 'number' && typeof memInfo.totalJSHeapSize === 'number') {
                        const usagePercent = memInfo.totalJSHeapSize > 0 ?
                            (memInfo.usedJSHeapSize / memInfo.totalJSHeapSize) * 100 : 0;

                        this.updatePerformanceMetrics({
                            memoryUsage: {
                                usedJSHeapSize: memInfo.usedJSHeapSize,
                                totalJSHeapSize: memInfo.totalJSHeapSize,
                                usagePercent
                            },
                            memoryPressure: usagePercent > this.CONFIG.MEMORY_PRESSURE_THRESHOLD,
                            lastHealthCheck: Date.now()
                        });
                    }
                } catch (memoryError) {
                    this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                        'Memory info not available:', memoryError);
                }
            }

            // Update push notification state if available
            if (typeof window !== 'undefined' && 'Notification' in window) {
                try {
                    this.updatePushNotificationState({
                        permission: Notification.permission,
                        currentStatus: Notification.permission
                    });
                } catch (notificationError) {
                    this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                        'Notification API not accessible:', notificationError);
                }
            }

            // Update service worker state
            const swState = this.getServiceWorkerState();
            if (swState !== this.appAnalyticsState.appDetails.serviceWorkerState) {
                this.updateAppDetails({ serviceWorkerState: swState });
            }

        } catch (error) {
            this.loggingService.log(LogLevel.WARN, AppAnalyticsService.COMPONENT_NAME,
                'Error during self-monitoring:', error);
        }
    }

    // Perform automatic logging
    private performAutoLog(): void {
        try {
            const appState = this.appAnalyticsState;

            // Create analytics payload from current app state (avoid circular references)
            const analyticsPayload = {
                time: Date.now(),
                angularVersion: appState.appDetails.version,
                connectedFromTime: appState.socketConnectionState.firstConnectedAt,
                browserId: appState.browserDetails.browserId,
                pushNotificationStatus: {
                    subscribed: appState.pushNotificationState.subscribed,
                    currentStatus: appState.pushNotificationState.currentStatus
                },
                socketAnalytics: {
                    totalConnectionAttempts: appState.socketConnectionState.totalConnectionAttempts,
                    totalReconnects: appState.socketConnectionState.totalReconnects,
                    totalDisconnects: appState.socketConnectionState.totalDisconnects,
                    totalMessagesSent: appState.socketConnectionState.totalMessagesSent,
                    totalMessagesReceived: appState.socketConnectionState.totalMessagesReceived,
                    sessionStartTime: appState.socketConnectionState.sessionStartTime
                },
                performanceMetrics: appState.performanceMetrics,
                browserDetails: appState.browserDetails,
                appDetails: appState.appDetails
            };

            this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                '[AppAnalyticsService]: Auto analytics gathered:', analyticsPayload);

            this.emitAnalyticsEvent('analytics_auto_logged', { payload: analyticsPayload });
        } catch (error) {
            this.loggingService.log(LogLevel.ERROR, AppAnalyticsService.COMPONENT_NAME,
                'Error during auto-logging:', error);
        }
    }

    // Send analytics data to server via socket
    private sendAnalyticsToServer(): void {
        this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
            'sendAnalyticsToServer: Timer callback triggered');

        if (this.isDestroyed) {
            this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                'sendAnalyticsToServer: Service is destroyed, skipping');
            return;
        }

        if (!this.socketService) {
            this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                'sendAnalyticsToServer: SocketService not available, skipping');
            return;
        }

        if (!this.appAnalyticsState) {
            this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                'sendAnalyticsToServer: AppAnalyticsState not available, skipping');
            return;
        }

        this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
            'sendAnalyticsToServer: All conditions met, proceeding with analytics transmission');

        try {
            // Get real socket connection state from SocketService
            const realSocketState: SocketConnectionState = this.socketService.getSocketConnectionState();
            
            this.loggingService.log(this.getLogLevel(), AppAnalyticsService.COMPONENT_NAME,
                'sendAnalyticsToServer: Retrieved real socket state:', realSocketState);
            
            // Merge socket states: sum up cumulative metrics, replace current state metrics
            const mergedSocketState: SocketConnectionState = {
                // Replace with current real-time values
                readyState: realSocketState.readyState,
                firstConnectedAt: realSocketState.firstConnectedAt,
                sessionStartTime: realSocketState.sessionStartTime,
                lastConnectionAt: realSocketState.lastConnectionAt,
                lastDisconnectionAt: realSocketState.lastDisconnectionAt,
                heartbeatStatus: realSocketState.heartbeatStatus,
                
                // Sum up cumulative metrics safely (real + existing analytics)
                totalConnectionAttempts: Math.max(0, (this.appAnalyticsState.socketConnectionState.totalConnectionAttempts || 0) + (realSocketState.totalConnectionAttempts || 0)),
                connectRetries: Math.max(0, (this.appAnalyticsState.socketConnectionState.connectRetries || 0) + (realSocketState.connectRetries || 0)),
                totalReconnects: Math.max(0, (this.appAnalyticsState.socketConnectionState.totalReconnects || 0) + (realSocketState.totalReconnects || 0)),
                totalDisconnects: Math.max(0, (this.appAnalyticsState.socketConnectionState.totalDisconnects || 0) + (realSocketState.totalDisconnects || 0)),
                totalMessagesSent: Math.max(0, (this.appAnalyticsState.socketConnectionState.totalMessagesSent || 0) + (realSocketState.totalMessagesSent || 0)),
                totalMessagesReceived: Math.max(0, (this.appAnalyticsState.socketConnectionState.totalMessagesReceived || 0) + (realSocketState.totalMessagesReceived || 0))
            };
            
            const analyticsPayload: AppAnalyticsState = {
                ...this.appAnalyticsState,
                socketConnectionState: mergedSocketState,
            };

            // Production safety: Check payload size before sending
            const payloadSize = JSON.stringify(analyticsPayload).length;
            if (payloadSize > 65536) { // 64KB limit
                this.loggingService.log(LogLevel.WARN, AppAnalyticsService.COMPONENT_NAME,
                    `Analytics payload too large (${payloadSize} bytes), skipping send`);
                return;
            }

            this.socketService.callApiViaSocket('log-app-analytics', analyticsPayload).subscribe({
                next: (response) => {
                    this.loggingService.log(this.getLogLevel(), AppAnalyticsService.COMPONENT_NAME,
                        'Analytics sent to server successfully. Payload size:', payloadSize, 'bytes');
                    this.emitAnalyticsEvent('analytics_server_sent', { response });
                },
                error: (error) => {
                    this.loggingService.log(LogLevel.ERROR, AppAnalyticsService.COMPONENT_NAME,
                        'Failed to send analytics to server. Payload size:', payloadSize, 'bytes. Error:', error);
                    this.emitAnalyticsEvent('analytics_server_error', { error });
                }
            });
        } catch (error) {
            this.loggingService.log(LogLevel.ERROR, AppAnalyticsService.COMPONENT_NAME,
                'Error preparing analytics for server:', error);
        }
    }

    // Set current user (call when user changes)
    setCurrentUser(userId: number | null): void {
        if (userId !== null && (typeof userId !== 'number' || isNaN(userId) || userId <= 0)) {
            this.loggingService.log(LogLevel.WARN, AppAnalyticsService.COMPONENT_NAME,
                'Invalid userId provided to setCurrentUser:', userId);
            return;
        }

        if (this.currentUserId !== userId) {
            // Save analytics for previous user before switching
            if (this.currentUserId) {
                this.saveAnalyticsToStorage();
            }

            this.currentUserId = userId;

            if (userId) {
                // Load analytics for new user
                this.loadAnalyticsFromStorage(userId);
            } else {
                // User logged out, reset analytics
                this.resetAnalytics();
            }
        }
    }

    // Record connection attempt
    recordConnectionAttempt(): void {
        const appState = this.appAnalyticsState;
        const newAttempts = appState.socketConnectionState.totalConnectionAttempts + 1;

        this.updateSocketConnectionState({
            totalConnectionAttempts: newAttempts,
            connectRetries: newAttempts
        });

        this.emitAnalyticsEvent('connection_attempt', {
            attempts: newAttempts
        });
        this.scheduleBatchSave();
    }

    // Record successful connection
    recordConnection(): void {
        const now = Date.now();
        const appState = this.appAnalyticsState;

        if (!appState.socketConnectionState.firstConnectedAt) {
            this.updateSocketConnectionState({
                firstConnectedAt: now
            });
        }

        this.updateSocketConnectionState({
            lastConnectionAt: now,
            connectRetries: 0
        });

        this.emitAnalyticsEvent('connected', {
            firstConnection: !appState.socketConnectionState.firstConnectedAt,
            timestamp: now
        });
        this.scheduleBatchSave();
    }

    // Record disconnection
    recordDisconnection(): void {
        const now = Date.now();
        const appState = this.appAnalyticsState;
        const newDisconnects = appState.socketConnectionState.totalDisconnects + 1;

        this.updateSocketConnectionState({
            lastDisconnectionAt: now,
            totalDisconnects: newDisconnects
        });

        this.emitAnalyticsEvent('disconnected', {
            disconnects: newDisconnects,
            timestamp: now
        });
        this.scheduleBatchSave();
    }

    // Record reconnection
    recordReconnection(): void {
        const appState = this.appAnalyticsState;
        const newReconnects = appState.socketConnectionState.totalReconnects + 1;

        this.updateSocketConnectionState({
            totalReconnects: newReconnects
        });

        this.emitAnalyticsEvent('reconnect', {
            reconnects: newReconnects
        });
        this.scheduleBatchSave();
    }

    // Record message sent
    recordMessageSent(): void {
        const appState = this.appAnalyticsState;
        const newMessagesSent = appState.socketConnectionState.totalMessagesSent + 1;

        this.updateSocketConnectionState({
            totalMessagesSent: newMessagesSent
        });

        this.emitAnalyticsEvent('message_sent', {
            totalSent: newMessagesSent
        });
        this.scheduleBatchSave();
    }

    // Record message received
    recordMessageReceived(): void {
        const appState = this.appAnalyticsState;
        const newMessagesReceived = appState.socketConnectionState.totalMessagesReceived + 1;

        this.updateSocketConnectionState({
            totalMessagesReceived: newMessagesReceived
        });

        this.emitAnalyticsEvent('message_received', {
            totalReceived: newMessagesReceived
        });
        this.scheduleBatchSave();
    }

    // Get current analytics snapshot
    getAnalytics(): SocketConnectionState {
        const appState = this.appAnalyticsState;
        return { ...appState.socketConnectionState };
    }

    // Get analytics with additional metrics
    getAnalyticsWithMetrics(): any {
        const now = Date.now();
        const appState = this.appAnalyticsState;
        const sessionDuration = appState.socketConnectionState.sessionStartTime ?
            now - appState.socketConnectionState.sessionStartTime : 0;

        return {
            // Core analytics
            sessionStartTime: appState.socketConnectionState.sessionStartTime,
            sessionDuration,
            sessionReconnects: appState.socketConnectionState.totalReconnects,
            sessionDisconnects: appState.socketConnectionState.totalDisconnects,
            sessionConnectionAttempts: appState.socketConnectionState.totalConnectionAttempts,
            sessionMessagesReceived: appState.socketConnectionState.totalMessagesReceived,
            sessionMessagesSent: appState.socketConnectionState.totalMessagesSent,
            firstConnectedAt: appState.socketConnectionState.firstConnectedAt,

            // Derived metrics
            averageMessagesPerMinute: sessionDuration > 0 ?
                ((appState.socketConnectionState.totalMessagesSent + appState.socketConnectionState.totalMessagesReceived) / (sessionDuration / 60000)) : 0,
            connectionSuccessRate: appState.socketConnectionState.totalConnectionAttempts > 0 ?
                ((appState.socketConnectionState.totalConnectionAttempts - appState.socketConnectionState.totalDisconnects) / appState.socketConnectionState.totalConnectionAttempts) : 1,

            // Metadata
            timestamp: now,
            userId: this.currentUserId
        };
    }

    // Reset analytics (for new session or logout)
    resetAnalytics(): void {
        const now = Date.now();
        this.updateSocketConnectionState({
            sessionStartTime: now,
            totalReconnects: 0,
            totalDisconnects: 0,
            totalConnectionAttempts: 0,
            totalMessagesReceived: 0,
            totalMessagesSent: 0,
            firstConnectedAt: null
        });

        this.emitAnalyticsEvent('session_start', {
            timestamp: now
        });
    }

    // Load analytics from storage for specific user
    loadAnalyticsFromStorage(userId: number): void {
        if (!userId || typeof userId !== 'number') {
            this.loggingService.log(LogLevel.WARN, AppAnalyticsService.COMPONENT_NAME,
                'Invalid userId provided to loadAnalyticsFromStorage');
            return;
        }

        try {
            const key = `socket_analytics_${userId}`;
            const stored = storageHelper.getFromLocalStorageWithExpiry(key);

            if (stored && stored.timestamp &&
                (Date.now() - stored.timestamp) < (this.CONFIG.STORAGE_EXPIRY * 1000)) {

                // Validate stored data structure
                if (this.isValidAnalyticsData(stored)) {
                    this.updateSocketConnectionState({
                        sessionStartTime: stored.sessionStartTime || Date.now(),
                        totalReconnects: stored.sessionReconnects || 0,
                        totalDisconnects: stored.sessionDisconnects || 0,
                        totalConnectionAttempts: stored.sessionConnectionAttempts || 0,
                        totalMessagesReceived: stored.sessionMessagesReceived || 0,
                        totalMessagesSent: stored.sessionMessagesSent || 0,
                        firstConnectedAt: stored.firstConnectedAt || null
                    });

                    this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                        `Loaded analytics for user ${userId}`);
                } else {
                    this.loggingService.log(LogLevel.WARN, AppAnalyticsService.COMPONENT_NAME,
                        `Invalid analytics data structure for user ${userId}, starting fresh`);
                    this.resetAnalytics();
                }
            } else if (stored) {
                // Data expired, clean it up
                this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                    `Analytics data expired for user ${userId}, cleaning up`);
                try {
                    localStorage.removeItem(key);
                } catch (storageError) {
                    this.loggingService.log(LogLevel.WARN, AppAnalyticsService.COMPONENT_NAME,
                        'Failed to remove expired analytics data:', storageError);
                }
                this.resetAnalytics();
            } else {
                // No data exists, start fresh
                this.resetAnalytics();
            }
        } catch (error) {
            this.loggingService.log(LogLevel.ERROR, AppAnalyticsService.COMPONENT_NAME,
                'Error loading analytics from storage:', error);
            this.resetAnalytics();
        }
    }

    // Validate analytics data structure
    private isValidAnalyticsData(data: any): boolean {
        return data &&
            typeof data === 'object' &&
            typeof data.timestamp === 'number' &&
            (data.sessionStartTime === undefined || typeof data.sessionStartTime === 'number') &&
            (data.sessionReconnects === undefined || typeof data.sessionReconnects === 'number') &&
            (data.sessionDisconnects === undefined || typeof data.sessionDisconnects === 'number') &&
            (data.sessionConnectionAttempts === undefined || typeof data.sessionConnectionAttempts === 'number') &&
            (data.sessionMessagesReceived === undefined || typeof data.sessionMessagesReceived === 'number') &&
            (data.sessionMessagesSent === undefined || typeof data.sessionMessagesSent === 'number');
    }

    // Save analytics to storage with batching
    saveAnalyticsToStorage(): void {
        if (!this.currentUserId) {
            return;
        }

        try {
            const key = `socket_analytics_${this.currentUserId}`;
            const appState = this.appAnalyticsState;
            const dataToSave = {
                sessionStartTime: appState.socketConnectionState.sessionStartTime,
                sessionReconnects: appState.socketConnectionState.totalReconnects,
                sessionDisconnects: appState.socketConnectionState.totalDisconnects,
                sessionConnectionAttempts: appState.socketConnectionState.totalConnectionAttempts,
                sessionMessagesReceived: appState.socketConnectionState.totalMessagesReceived,
                sessionMessagesSent: appState.socketConnectionState.totalMessagesSent,
                firstConnectedAt: appState.socketConnectionState.firstConnectedAt,
                timestamp: Date.now(),
                userId: this.currentUserId
            };

            // Try to save to storage with quota handling
            try {
                storageHelper.setToLocalStorageWithExpiry(key, dataToSave, this.CONFIG.STORAGE_EXPIRY * 1000);
                this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                    `Analytics saved for user ${this.currentUserId}`);
            } catch (quotaError: any) {
                // Handle storage quota exceeded
                if (quotaError.name === 'QuotaExceededError' || quotaError.code === 22) {
                    this.loggingService.log(LogLevel.WARN, AppAnalyticsService.COMPONENT_NAME,
                        'Storage quota exceeded, clearing old analytics data');

                    // Try to clear some space by removing old keys
                    try {
                        const keysToRemove = [];
                        for (let i = 0; i < localStorage.length; i++) {
                            const storageKey = localStorage.key(i);
                            if (storageKey && storageKey.startsWith('socket_analytics_') &&
                                storageKey !== key) {
                                keysToRemove.push(storageKey);
                            }
                        }

                        if (keysToRemove.length > 0) {
                            storageHelper.removeMultipleKeys(keysToRemove);
                            // Try saving again
                            storageHelper.setToLocalStorageWithExpiry(key, dataToSave, this.CONFIG.STORAGE_EXPIRY * 1000);
                            this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                                'Analytics saved after clearing old data');
                        } else {
                            this.loggingService.log(LogLevel.ERROR, AppAnalyticsService.COMPONENT_NAME,
                                'Unable to save analytics: storage full and no old data to clear');
                        }
                    } catch (cleanupError) {
                        this.loggingService.log(LogLevel.ERROR, AppAnalyticsService.COMPONENT_NAME,
                            'Failed to cleanup storage:', cleanupError);
                    }
                } else {
                    throw quotaError;
                }
            }

            this.emitAnalyticsEvent('analytics_saved', {
                userId: this.currentUserId,
                timestamp: dataToSave.timestamp
            });

        } catch (error) {
            this.loggingService.log(LogLevel.ERROR, AppAnalyticsService.COMPONENT_NAME,
                'Error saving analytics to storage:', error);
        }
    }

    // Schedule batched save to reduce storage operations
    private scheduleBatchSave(): void {
        if (this.batchTimeout) {
            clearTimeout(this.batchTimeout);
        }

        this.batchTimeout = setTimeout(() => {
            this.saveAnalyticsToStorage();
            this.batchTimeout = null;
        }, this.CONFIG.BATCH_DELAY);
    }

    // Emit analytics event
    private emitAnalyticsEvent(type: AnalyticsEvent['type'], data?: any): void {
        this.analyticsEventsSubject.next({
            type,
            timestamp: Date.now(),
            data
        });
    }

    // Force immediate save (for critical events like logout)
    forceSave(): void {
        if (this.batchTimeout) {
            clearTimeout(this.batchTimeout);
            this.batchTimeout = null;
        }
        this.saveAnalyticsToStorage();
    }

    // Check if service is in healthy state
    isHealthy(): boolean {
        try {
            return !!(this.appAnalyticsState &&
                this.analyticsEventsSubject &&
                !this.analyticsEventsSubject.closed);
        } catch {
            return false;
        }
    }

    // Get service status for debugging
    getServiceStatus(): any {
        return {
            healthy: this.isHealthy(),
            currentUserId: this.currentUserId,
            autoLogTimerActive: this.timers.has('autoLog'),
            monitoringTimerActive: this.timers.has('monitoring'),
            serverSyncTimerActive: this.timers.has('serverSync'),
            batchTimeoutActive: !!this.batchTimeout,
            analyticsEventsSubjectClosed: this.analyticsEventsSubject?.closed || false
        };
    }

    // Cleanup resources
    destroy(): void {
        try {
            // Clear all timers first
            this.clearAllTimers();

            // Unsubscribe from SwUpdate
            if (this.swUpdateSubscription) {
                this.swUpdateSubscription.unsubscribe();
                this.swUpdateSubscription = undefined;
            }

            // Save any pending analytics
            this.forceSave();

            // Emit session end
            const appState = this.appAnalyticsState;
            this.emitAnalyticsEvent('session_end', {
                sessionDuration: appState.socketConnectionState.sessionStartTime ?
                    Date.now() - appState.socketConnectionState.sessionStartTime : 0
            });

            // Complete subjects
            this.analyticsEventsSubject.complete();

            this.loggingService.log(LogLevel.DEBUG, AppAnalyticsService.COMPONENT_NAME,
                'Service destroyed and resources cleaned up');
        } catch (error) {
            this.loggingService.log(LogLevel.ERROR, AppAnalyticsService.COMPONENT_NAME,
                'Error during service destruction:', error);
        }
    }

    // Angular lifecycle hook for cleanup
    ngOnDestroy(): void {
        this.destroy();
    }
}
